import React from 'react';

import type { UUID } from '@aline/common';
import type { AlertColor } from '@mui/material';
import { Alert, IconButton } from '@mui/material';
import { ArrowDown } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { createClampStyles } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import type { ReviewAssignment } from './task-review-helpers';
import {
  getHasAssigneeSide,
  getHasReviewers,
  getTaskIsOpen,
  getTaskTurn,
  getTaskTurnText,
} from './task-review-helpers';

const useStyles = makeStyles()(theme => ({
  action: {
    alignItems: 'center',
    marginLeft: theme.spacing(0.5),
    marginRight: 0,
    padding: 0,
  },
  alert: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
    flexShrink: 0,
    justifyContent: 'center',
    padding: theme.spacing(0, 1.5, 0, 2),
  },
  message: {
    ...createClampStyles(2),
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.4,
    padding: theme.spacing(0.75, 0),
  },
}));

const TASK_ALERT = {
  ChangesRequested: 'changesRequested',
  MissingAssignee: 'missingAssignee',
  Rejected: 'rejected',
} as const;
type TaskAlert = (typeof TASK_ALERT)[keyof typeof TASK_ALERT];

const ALERT_SEVERITY = {
  [TASK_ALERT.ChangesRequested]: 'warning',
  [TASK_ALERT.MissingAssignee]: 'warning',
  [TASK_ALERT.Rejected]: 'error',
} satisfies Record<TaskAlert, AlertColor>;

interface AlertTask {
  assignments: readonly ReviewAssignment[];
  completedAt: string | null;
  counterpartyName: string | null;
  isDelegatedToAi: boolean;
  rejectedAt: string | null;
  status: Apollo.TaskStatus;
}

interface TaskReviewStatusAlertProps {
  /** Scrolls to the assignees card; null when there is no card to show who decided. */
  onJumpToReviewers: (() => void) | null;
  task: AlertTask;
}

/** A banner of up to two lines for the review states that need the reader's attention. */
export function TaskReviewStatusAlert({
  onJumpToReviewers,
  task,
}: TaskReviewStatusAlertProps): JSX.Element | null {
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const alert = getTaskAlert(task);
  if (!alert) return null;
  const message = getAlertMessage(alert, { currentUserId, task });

  return (
    <Alert
      action={
        onJumpToReviewers && (
          <AlineTooltip title="See assignees">
            <IconButton
              aria-label="See assignees"
              color="inherit"
              size="small"
              onClick={onJumpToReviewers}
            >
              <ArrowDown size={16} />
            </IconButton>
          </AlineTooltip>
        )
      }
      classes={{ action: classes.action, message: classes.message, root: classes.alert }}
      severity={ALERT_SEVERITY[alert]}
      title={message}
    >
      {message}
    </Alert>
  );
}

function getTaskAlert(task: AlertTask): TaskAlert | null {
  if (task.status === Apollo.TaskStatus.Rejected) return TASK_ALERT.Rejected;
  if (!getTaskIsOpen(task)) return null;
  if (getHasReviewers(task.assignments) && !getHasAssigneeSide(task))
    return TASK_ALERT.MissingAssignee;
  // In review needs no banner: the status badge and each reviewer's chip already say it.
  if (task.status === Apollo.TaskStatus.ChangesRequested) return TASK_ALERT.ChangesRequested;
  return null;
}

function getAlertMessage(
  alert: TaskAlert,
  options: { currentUserId: UUID | null; task: AlertTask }
): string {
  if (alert === TASK_ALERT.Rejected) return 'Task rejected';
  if (alert === TASK_ALERT.MissingAssignee)
    return 'Reviewers are waiting on an assignee. Add someone to do the work.';
  const turn = getTaskTurn(options.task, options.currentUserId);
  return turn ? getTaskTurnText(turn) : '';
}
